import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { denormalisedEntities, ensureCurrentUser } from '../../util/data';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { loadData, getUserExternalReview } from '../../containers/ProfilePage/ProfilePage.duck';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
    Page,
    LayoutSingleColumn,
    LayoutWrapperTopbar,
    LayoutWrapperMain,
    LayoutWrapperFooter,
    Footer,
    NamedRedirect,
    NoResultsBlock,
    EmailConfirmationBar,
    LowBalanceNotificationBar,
    Button,
    Breadcrumb,
} from '../../components';
import { promptUserBio as promptUserBioRequest } from '../OnboardingPage/OnboardingPage.duck';
import { ProfileSettingsForm } from '../../forms';
import { TopbarContainer } from '../../containers';
import { updateProfile, uploadImage } from './ProfileSettingsPage.duck';
import css from './ProfileSettingsPage.css';
import config from '../../config';
import EditProfileBar from './EditProfileBar';
import { getEntityAssets, removeEntityAssets, uploadEntityAssets } from '../../ducks/Assets.duck';
import { useIsMobile } from '../../hooks/useIsMobile';
import classNames from 'classnames';
import { useScrollY } from '../../hooks/useScrollY';
import { parse } from '../../util/urlHelpers';
import { ArrowNextIcon } from '../../icons';
import { updateUserViewsNumber, updateUserVisitorsData } from '../../util/api';
import InvitesModal from '../ConfirmationPage/InvitesModal';
import { ONBOARDING_INVITES_SENT } from '../OnboardingPage/OnboardingPage';
import { userIsDeletedOrBanned, userIsNotAvailable } from '../../util/user';
import { updateUserProfileInfo } from '../../ducks/user.duck';
import { deanonymizeUser, shouldUserBeDeanonymizedForUser } from '../../util/anonym';
import { ASSET_ENTITY_TYPE_USER } from '../../marketplace-custom-config';
import { getSeoBreadcrumbsPaths } from '../../util/seo';
import { selectLocation, selectLocationRequestInProgress } from '../../ducks/locations.duck';
import { trimDisplayNameLastWord } from '../../util/text';

const sessionStorageDefined = typeof window !== 'undefined';

const { userTypeRider, userTypeHorseowner } = config;

const { UUID } = sdkTypes;

const ProfileSettingsPage = ({ isPublic, userShowRequested }) => {
    const intl = useIntl();
    const history = useHistory();
    const location = useLocation();
    const params = useParams();
    const dispatch = useDispatch();

    const { currentUser, currentUserRequestInProgress } = useSelector(state => state.user);
    const scrollingDisabled = useSelector(isScrollingDisabled);
    const {
        image,
        uploadImageError,
        uploadInProgress,
        updateInProgress,
        updateProfileError,
        horseownerListings,
        horseownerListingsInProgress,
    } = useSelector(state => state['ProfileSettingsPage']);
    const {
        userId,
        externalReviewsErrors,
        externalReviewsRequests,
        externalReviewsData,
        userShowLoading,
        activeTransactionsDataInProgress,
        activeTransactionsDataError,
        activeTransactionsData,
    } = useSelector(state => state['ProfilePage']);
    const {
        userDocErrorMetadata, // ?
        promptUserBioInProgress,
        promptUserBioError,
        promptUserBio,
    } = useSelector(state => state['OnboardingPage']);
    const marketplaceEntities = useSelector(state => state['marketplaceData'].entities);

    /** denormalised once per entities change, so that publicUser keeps its reference */
    const publicUser = useMemo(() => {
        const userMatches = denormalisedEntities(
            marketplaceEntities,
            [{ type: 'user', id: userId }],
            false
        );
        return userMatches.length === 1 ? userMatches[0] : null;
    }, [marketplaceEntities, userId]);

    const actions = useMemo(
        () => ({
            onImageUpload: data => dispatch(uploadImage(data)),
            onUpdateProfile: (data, publicData) => dispatch(updateProfile(data, publicData)),
            // TODO use onGetEntityAssets instead
            onDocsListRequest: userId => dispatch(getEntityAssets({ id: userId })),
            onUploadSingleDocRequest: data =>
                dispatch(uploadEntityAssets({ ...data, entityType: ASSET_ENTITY_TYPE_USER })),
            onRemoveSingleDocRequest: (Key, userId) =>
                dispatch(removeEntityAssets({ entityId: userId, idToDelete: Key })),
            onPromptUserBio: () => dispatch(promptUserBioRequest()),
        }),
        [dispatch]
    );
    const { onImageUpload, onUpdateProfile } = actions;

    const [isMobile, , computing] = useIsMobile(1024);
    const [scrollY] = useScrollY();

    const [invitesModalOpen, setInvitesModalOpen] = useState(
        () => sessionStorageDefined && sessionStorage.getItem(ONBOARDING_INVITES_SENT)
    );

    const { mobilemenu } = parse(location.search);
    /**
     * Deanonymization logic for public user profiles
     * This code reveals the profile of a public rider to a horse owner.
     * This happens when the owner has a transaction with the rider whose page is being viewed,
     * and a rider disabled the anonymity setting during the inquiry step.
     *
     * publicUser - rider
     * currentUser - horseowner, with transaction deanonymized
     *
     */
    const shouldBeDeanonymized = shouldUserBeDeanonymizedForUser(currentUser, publicUser);

    const ensuredUser = ensureCurrentUser(isPublic ? publicUser : currentUser);
    const user = shouldBeDeanonymized ? deanonymizeUser(ensuredUser) : ensuredUser;
    const isLoggedOut = !currentUser || !currentUser.id;

    const userIdData = user ? user.id : null;
    const userUuid = userIdData ? userIdData.uuid : null;

    const {
        attributes: { emailVerified, email },
    } = currentUser || { attributes: {} };

    const { firstName, lastName, bio, displayName, publicData } = user.attributes.profile;
    const {
        age,
        licence,
        experience,
        language,
        drivingLicense,
        auto,
        galleryImages,
        riderQualification,
        riderCertification,
        userType,
    } = publicData;
    const { userType: userTypeParam } = params;

    const userLocation = useSelector(state =>
        selectLocation(state, publicData.country, publicData.postalCode)
    );
    const userLocationLoading = useSelector(state =>
        selectLocationRequestInProgress(state, publicData.country, publicData.postalCode)
    );

    const userNotAvailable =
        (userShowRequested && userIsNotAvailable(publicUser)) ||
        (isPublic && !userShowLoading && !publicUser && !activeTransactionsDataInProgress);
    /** allow non authenticated users view riders profiles */
    const currentUserNotAvailable = userIsDeletedOrBanned(currentUser);

    const isRider =
        currentUser && currentUser.id
            ? userTypeRider === currentUser.attributes.profile.publicData.userType
            : false;

    const { userType: publicUserType } = publicUser?.attributes?.profile?.publicData || {};
    const { userType: currentUserType } = currentUser?.attributes?.profile?.publicData || {};

    const publicUserIsRider =
        publicUser && (publicUser.attributes.profile.publicData || {}).userType === userTypeRider;

    const isSameUser =
        currentUser && currentUser.id && user && user.id && currentUser.id.uuid === user.id.uuid;

    useEffect(() => {
        /** update public user visitors data */
        if (isSameUser) return;
        if (isLoggedOut) return;
        if (!isPublic) return;
        if (!publicUser) return;
        if (!publicUserType) return;
        if (!currentUserType) return;

        const { mainHorseId } = currentUser.attributes.profile.publicData;

        if (currentUserType !== userTypeHorseowner || !mainHorseId) return;
        if (publicUserType !== userTypeRider) return;
        /**
         * visitedEntryId - riderId
         * visitorRepresentationId - mainHorseId
         */
        const riderId = publicUser.id.uuid;
        updateUserVisitorsData(riderId, riderId, mainHorseId, 'rider');
    }, [isSameUser, isLoggedOut, publicUserType, currentUserType, isPublic, userUuid]);

    useEffect(() => {
        if (!userUuid) return;

        dispatch(getUserExternalReview(userUuid));
    }, [userUuid]);

    useEffect(() => {
        /**
         * update viewsNum field for riders only
         * when any user (but not the same) logged-in/out
         * visits the page
         */
        if (isSameUser) return;
        if (!isPublic) return;
        if (!publicUser) return;
        if (!publicUserType) return;
        if (publicUserType !== userTypeRider) return;

        updateUserViewsNumber(userUuid);
    }, [isSameUser, isPublic, userUuid, publicUserType]);

    if (userTypeParam && userType && userTypeParam !== userType) {
        return (
            <NamedRedirect name="ProfileSettingsPage" params={{ userType: userType || 'rider' }} />
        );
    }

    const inquiryDisabled = isRider && publicUserIsRider;

    const profileImageId = user.profileImage ? user.profileImage.id : null;
    const profileImage = image || { imageId: profileImageId };
    /**
     * Navigation bar is shown not only for the current user
     * but also when other user comes to rider profile page
     */
    const showNavBar = !computing && !!scrollY && isPublic && !mobilemenu;
    const galleryImagesMaybe =
        galleryImages && typeof galleryImages === 'string' ? JSON.parse(galleryImages) : null;

    const handleSubmit = ({
        firstName,
        lastName,
        bio: rawBio,
        location,
        age,
        licence,
        experience,
        language,
        drivingLicense,
        auto,
        galleryImages,
        riderQualification,
        riderCertification,
    }) => {
        const publicData = {
            location,
            age,
            licence,
            experience,
            language,
            drivingLicense: !!drivingLicense,
            auto: !!auto,
            emailVerified: currentUser.attributes.emailVerified,
            galleryImages: JSON.stringify(galleryImages),
            riderQualification,
            riderCertification,
        };
        // Ensure that the optional bio is a string
        const bio = rawBio || '';

        const profile = {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            bio,
        };
        const uploadedImage = image;

        // Update profileImage only if file system has been accessed
        const updatedValues =
            uploadedImage && uploadedImage.imageId && uploadedImage.file
                ? { ...profile, profileImageId: uploadedImage.imageId }
                : profile;

        onUpdateProfile(updatedValues, publicData);
    };

    const onImageUploadHandler = async (values, fn) => {
        const { id, imageId, file, isDefault = false } = values;
        if (file) {
            const params = {
                publicData: {
                    hasDefaultAvatar: isDefault || null,
                },
            };

            await dispatch(updateUserProfileInfo(params));
            return await fn({ id, imageId, file, isDefault });
        }
    };

    const showForm =
        user.id && !userNotAvailable && !currentUserRequestInProgress && !currentUserNotAvailable;

    const profileSettingsForm = showForm ? (
        <ProfileSettingsForm
            className={css.form}
            user={user}
            currentUser={ensureCurrentUser(currentUser)}
            publicUser={publicUser}
            inquiryDisabled={inquiryDisabled}
            isLoggedOut={isLoggedOut}
            isSameUser={isSameUser}
            isMobile={isMobile}
            computing={computing}
            showNavBar={showNavBar}
            initialValues={{
                firstName,
                lastName,
                bio,
                profileImage: user.profileImage,
                age,
                licence,
                experience,
                language,
                drivingLicense,
                auto,
                galleryImages: galleryImagesMaybe,
                riderQualification,
                riderCertification,
            }}
            profileImage={profileImage}
            onImageUpload={e => onImageUploadHandler(e, onImageUpload)}
            onSubmit={handleSubmit}
            onUpdateProfile={onUpdateProfile}
            isPublic={isPublic}
            updateInProgress={updateInProgress}
            updateProfileError={updateProfileError}
            uploadImageError={uploadImageError}
            uploadInProgress={uploadInProgress}
            userDocErrorMetadata={userDocErrorMetadata}
            externalReviewsErrors={externalReviewsErrors}
            externalReviewsRequests={externalReviewsRequests}
            externalReviewsData={externalReviewsData}
            horseownerListings={horseownerListings}
            horseownerListingsInProgress={horseownerListingsInProgress}
            promptUserBioInProgress={promptUserBioInProgress}
            promptUserBioError={promptUserBioError}
            promptUserBio={promptUserBio}
            activeTransactionsDataInProgress={activeTransactionsDataInProgress}
            activeTransactionsDataError={activeTransactionsDataError}
            activeTransactionsData={activeTransactionsData}
            onDocsListRequest={actions.onDocsListRequest}
            onUploadSingleDocRequest={actions.onUploadSingleDocRequest}
            onRemoveSingleDocRequest={actions.onRemoveSingleDocRequest}
            onPromptUserBio={actions.onPromptUserBio}
            dispatch={dispatch}
            params={params}
        />
    ) : null;
    const pageUserIsRider = userType === userTypeRider;

    const [userDisplayName] = userUuid ? user.attributes.profile.displayName.split(' ') : [];
    const userPublicName = firstName && lastName ? `${firstName} ${lastName}` : userDisplayName;

    const title = pageUserIsRider
        ? `Reiter-Profil von ${userPublicName} | HorseDeal`
        : intl.formatMessage({ id: 'ProfileSettingsPage.title' });
    const description = pageUserIsRider
        ? `Disziplinen, Niveau, Qualifikationen, Auszeichnungen und Ausbildungen von ${userPublicName}`
        : null;
    const noIndexTag = [
        {
            name: 'robots',
            content: 'noindex',
        },
    ];

    const userBreadcrumbsPaths = getSeoBreadcrumbsPaths({
        countryCode: publicData.country,
        activities: publicData.interest,
        title: trimDisplayNameLastWord(displayName),
        location: userLocation,
    });

    const showTopBarNonPublic = isSameUser || (isPublic ? !computing && !isMobile : true);
    const showTopBarPublic = isPublic && !computing && isMobile && !isSameUser;
    const showUserBreadcrumbs =
        pageUserIsRider && isPublic && !isSameUser && (userBreadcrumbsPaths || userLocationLoading);

    const userAvatar =
        user.profileImage && user.profileImage.attributes
            ? (
                  user.profileImage.attributes.variants['square-small2x'] ||
                  user.profileImage.attributes.variants['square-small'] ||
                  user.profileImage.attributes.variants['landscape-crop'] ||
                  {}
              ).url
            : null;

    const pageImagesProps =
        userAvatar && pageUserIsRider
            ? {
                  facebookImages: [
                      {
                          name: 'facebook',
                          url: userAvatar,
                          width: 480,
                          height: 480,
                      },
                  ],
                  twitterImages: [
                      {
                          name: 'twitter',
                          url: userAvatar,
                          width: 480,
                          height: 480,
                      },
                  ],
                  logo: userAvatar,
              }
            : {};
    return (
        <Page
            className={css.root}
            scrollingDisabled={scrollingDisabled}
            metaTags={userNotAvailable ? noIndexTag : null}
            title={title}
            description={description}
            {...pageImagesProps}
        >
            <LayoutSingleColumn>
                <LayoutWrapperTopbar>
                    {isMobile && (
                        <>
                            <EmailConfirmationBar />
                            <LowBalanceNotificationBar />
                        </>
                    )}

                    {showTopBarNonPublic && (
                        <TopbarContainer
                            currentPage="ProfileSettingsPage"
                            desktopClassName={classNames({
                                [css.topbarDesktop]: showNavBar,
                            })}
                            showEmailNotificationBar={!isMobile}
                        />
                    )}
                    {showTopBarPublic && (
                        <nav className={css.topbarMob}>
                            <Button onClick={() => history.goBack()}>
                                <ArrowNextIcon />
                                <span>Zurück</span>
                            </Button>
                        </nav>
                    )}
                    {invitesModalOpen && (
                        <InvitesModal
                            isOpen
                            onClose={() => {
                                sessionStorage.removeItem(ONBOARDING_INVITES_SENT);
                                setInvitesModalOpen(false);
                            }}
                        />
                    )}
                </LayoutWrapperTopbar>
                <LayoutWrapperMain
                    blobBackground={!!userNotAvailable}
                    className={classNames({
                        [css.userNotAvailable]: !!userNotAvailable,
                    })}
                >
                    <div
                        className={classNames({
                            [css.content]: !userNotAvailable,
                            [css.withBreadcrumbs]: showUserBreadcrumbs,
                        })}
                    >
                        {showUserBreadcrumbs && (
                            <nav>
                                <Breadcrumb
                                    loading={userLocationLoading}
                                    paths={userBreadcrumbsPaths}
                                    urlPrefix="/landing"
                                    rootClassName={css.breadcrumbs}
                                />
                            </nav>
                        )}
                        {isPublic && isSameUser && (
                            <EditProfileBar
                                userType={userType}
                                emailNotVerified={!emailVerified && email}
                                currentUser={currentUser}
                                scrolled={showNavBar}
                            />
                        )}
                        {profileSettingsForm}
                        {userNotAvailable && (
                            <NoResultsBlock
                                headerId="Dieses Profil existiert nicht mehr."
                                parapraphId="Das aufgerufene Profil wurde gelöscht und existiert nicht mehr."
                                rootClass={css.noResultsRoot}
                                isSearchForm
                            />
                        )}
                    </div>
                </LayoutWrapperMain>
                <LayoutWrapperFooter>
                    <Footer />
                </LayoutWrapperFooter>
            </LayoutSingleColumn>
        </Page>
    );
};

const { bool } = PropTypes;

ProfileSettingsPage.propTypes = {
    isPublic: bool,
};

ProfileSettingsPage.loadData = params => {
    const id = new UUID(params.id);
    return loadData(id);
};

export default ProfileSettingsPage;
